import type { Transition, Variants } from "framer-motion";

export type OrganizePose = {
  x: number;
  y: number;
  z?: number;
  rotateX: number;
  rotateY: number;
  rotateZ: number;
  scale?: number;
  opacity?: number;
};

const NO_ROTATE = { rotateX: 0, rotateY: 0, rotateZ: 0 };

export const CARD_RAIL_OFFSET = "-108%";

export const CHAOS_POSES = {
  mockup: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 1 },
  impressions: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
  clicks: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
  calls: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
} as const satisfies Record<string, OrganizePose>;

export const CHAOS_POSES_MOBILE = {
  mockup: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 1 },
  impressions: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
  clicks: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
  calls: { x: 0, y: 0, z: 0, ...NO_ROTATE, scale: 1, opacity: 0 },
} as const satisfies Record<string, OrganizePose>;

const ORGANIZED_POSE = {
  x: 0,
  y: 0,
  z: 0,
  rotateX: 0,
  rotateY: 0,
  rotateZ: 0,
  scale: 1,
  opacity: 1,
  filter: "blur(0px)",
};

export const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

export const INTRO_DURATION = 0.48;
export const CHAOS_HOLD_MS = 0;
export const ORGANIZE_DURATION = 0.55;
export const CARD_ORGANIZE_DELAYS = [0, 0.05, 0.1] as const;
export const FINAL_OVERLAP_MS = 320;

export const INTRO_TRANSITION: Transition = {
  duration: INTRO_DURATION,
  ease: SEQUENCE_EASE,
};

export const INTRO_BLUR_AMOUNT = "14px";

export const INTRO_HIDDEN = {
  opacity: 0,
  filter: `blur(${INTRO_BLUR_AMOUNT})`,
  scale: 0.98,
};

export const INTRO_VISIBLE = {
  opacity: 1,
  filter: "blur(0px)",
  scale: 1,
};

export const MOCKUP_INTRO_HIDDEN = {
  opacity: 0,
  filter: `blur(${INTRO_BLUR_AMOUNT})`,
};

export const MOCKUP_INTRO_VISIBLE = {
  opacity: 1,
  filter: "blur(0px)",
};

export const MOCKUP_CENTER_OFFSET_MD = 94;
export const MOCKUP_CENTER_OFFSET_LG = 108;

export const ORGANIZE_TRANSITION: Transition = {
  duration: ORGANIZE_DURATION,
  ease: SEQUENCE_EASE,
};

export const SCENE_CHAOS_POSE = { rotateX: 0, rotateY: 0, scale: 1 };
export const SCENE_FINAL_POSE = { rotateX: 4, rotateY: -8, scale: 1 };

export const SCENE_FINAL_TRANSITION: Transition = {
  duration: 0.58,
  ease: SEQUENCE_EASE,
};

export const CONTAINER_BOUNCE_TRANSITION: Transition = {
  duration: 0.48,
  ease: [0.34, 1.45, 0.64, 1],
};

export const FINAL_CTA_DELAY = 0.08;
export const FINAL_GLOW_DELAY = 0;

export function getOrganizeStartMs() {
  return INTRO_DURATION * 1000 + CHAOS_HOLD_MS;
}

export function getOrganizeCompleteMs() {
  const lastCardDelay = CARD_ORGANIZE_DELAYS[CARD_ORGANIZE_DELAYS.length - 1];
  return getOrganizeStartMs() + (lastCardDelay + ORGANIZE_DURATION) * 1000 - FINAL_OVERLAP_MS;
}

export function createCardRailVariants(reducedMotion: boolean, delay = 0): Variants {
  if (reducedMotion) {
    return {
      hidden: { opacity: 0 },
      chaos: { opacity: 0 },
      organized: {
        ...ORGANIZED_POSE,
        transition: { duration: 0.25, ease: "easeOut" },
      },
    };
  }

  const railStart = {
    opacity: 0,
    x: CARD_RAIL_OFFSET,
    y: 0,
    z: 0,
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
    scale: 1,
  };

  return {
    hidden: railStart,
    chaos: {
      ...railStart,
      transition: { duration: 0 },
    },
    organized: {
      ...ORGANIZED_POSE,
      transition: {
        ...ORGANIZE_TRANSITION,
        delay,
      },
    },
  };
}
